import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AgentAvatar from "../AgentAvatar";
import { useStatusPalette } from "../../../utils/statusColors";
import MarkdownContent from "../MarkdownContent";
import useAgentStore from "../useAgentStore";
import { buildAgentChatUrl } from "../boards/commentUtils";
import {
  AVATAR_SIZE,
  AVATAR_RADIUS,
  TEXT_INDENT,
} from "./conversationConstants";
import {
  messageRowSx,
  formatMessageTime,
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
  buildResponseReqConfig,
  stripInterAgentHeader,
} from "./conversationUtils";

function ResponseRequirementBadge({ responseRequirement }) {
  const _sp = useStatusPalette();
  const RESPONSE_REQ_CONFIG = buildResponseReqConfig(_sp);
  const cfg = RESPONSE_REQ_CONFIG[responseRequirement];
  if (!cfg) return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: "18px",
        px: "6px",
        borderRadius: "4px",
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 500,
          color: cfg.color,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

export function CorrelationIdBadge({ correlationId }) {
  const _sp = useStatusPalette();
  if (!correlationId) return null;
  const short =
    correlationId.length > 8 ? correlationId.slice(0, 8) : correlationId;
  return (
    <Tooltip title={`Correlation ID: ${correlationId}`} placement="top" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height: "18px",
          px: "6px",
          borderRadius: "4px",
          backgroundColor: "action.hover",
          border: 1,
          borderColor: "divider",
          cursor: "default",
        }}
      >
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 500,
            color: _sp.purple.color,
            lineHeight: 1,
            whiteSpace: "nowrap",
            fontFamily: "monospace",
          }}
        >
          {short}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export function WorkflowMessage({
  content,
  timestamp,
  projectId,
  projectName,
  workflowSessionId,
}) {
  const sessionHref =
    projectId && workflowSessionId
      ? `/project/logs/progress_view/${projectId}/${workflowSessionId}`
      : null;

  const workflowLabel = projectName || "Workflow";

  return (
    <Box sx={{ ...messageRowSx, pt: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <Box
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            borderRadius: AVATAR_RADIUS,
            flexShrink: 0,
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AccountTreeOutlinedIcon style={{ fontSize: 16, color: "#fff" }} />
        </Box>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
            }}
          >
            <CallReceivedIcon sx={{ fontSize: 14, color: "primary.main" }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "text.secondary",
                flex: 1,
              }}
            >
              From:{" "}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {workflowLabel}
              </Box>
            </Typography>
            {sessionHref && (
              <Box
                component="a"
                href={sessionHref}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "primary.main",
                  textDecoration: "none",
                  flexShrink: 0,
                  transition: "all 100ms ease",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  },
                }}
              >
                View session
                <OpenInNewIcon sx={{ fontSize: 11 }} />
              </Box>
            )}
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.primary",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {content}
            </Typography>
          </Box>
          {timestamp && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                {formatMessageTime(timestamp)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function WorkflowResponseMessage({
  content,
  agentName,
  agentId,
  timestamp,
  grouped,
  projectId,
  projectName,
  workflowSessionId,
  onOpenAgentSettings,
}) {
  const name = agentName || "Agent";
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const sessionHref =
    projectId && workflowSessionId
      ? `/project/logs/progress_view/${projectId}/${workflowSessionId}`
      : null;
  const workflowLabel = projectName
    ? `Response for ${projectName}`
    : "Workflow output";

  const formatJson = (val) => {
    if (!val) return "";
    try {
      return JSON.stringify(JSON.parse(val), null, 2);
    } catch {
      return val;
    }
  };

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar agentId={agentId} name={name} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
            }}
          >
            <CallMadeIcon sx={{ fontSize: 14, color: "primary.main" }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "text.secondary",
                flex: 1,
              }}
            >
              {workflowLabel}
            </Typography>
            {sessionHref && (
              <Box
                component="a"
                href={sessionHref}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "primary.main",
                  textDecoration: "none",
                  flexShrink: 0,
                  transition: "all 100ms ease",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  },
                }}
              >
                View session
                <OpenInNewIcon sx={{ fontSize: 11 }} />
              </Box>
            )}
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Box
              sx={{
                p: 1,
                backgroundColor: "action.hover",
                borderRadius: "6px",
                maxHeight: "300px",
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: (theme) => `${theme.palette.divider} transparent`,
              }}
            >
              <Typography
                component="pre"
                sx={{
                  fontSize: "12px",
                  fontFamily:
                    '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                  color: "text.secondary",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  m: 0,
                  lineHeight: 1.5,
                }}
              >
                {formatJson(content)}
              </Typography>
            </Box>
          </Box>
          {timestamp && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                {formatMessageTime(timestamp)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function AgentDetailPopover({ anchorEl, open, onClose, agentId }) {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !agentId) return;
    let cancelled = false;
    setLoading(true);
    useAgentStore.getState().fetchAgent(agentId).then((data) => {
      if (!cancelled) {
        setAgent(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, agentId]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
            border: "1px solid",
            borderColor: "divider",
            mt: 0.5,
          },
        },
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ color: "primary.main" }} />
        </Box>
      ) : agent ? (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
          >
            <AgentAvatar agent={agent} name={agent.name} size={36} borderRadius="8px" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {agent.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "text.disabled",
                  lineHeight: 1.3,
                  mt: 0.25,
                }}
              >
                {agent.model || "Default model"}
              </Typography>
            </Box>
          </Box>
          {agent.description && (
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                lineHeight: 1.5,
                mb: 1.5,
              }}
            >
              {agent.description}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              component="a"
              href={`/agents/${agentId}`}
              onClick={onClose}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.75,
                borderRadius: "6px",
                backgroundColor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
                fontSize: "12px",
                fontWeight: 500,
                color: "text.primary",
                textDecoration: "none",
                cursor: "pointer",
                transition: "all 100ms ease",
                "&:hover": { backgroundColor: "action.selected" },
              }}
            >
              View agent
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: "13px", color: "text.disabled" }}>
            Agent not found
          </Typography>
        </Box>
      )}
    </Popover>
  );
}

export function InterAgentMessage({
  content,
  timestamp,
  senderAgentId,
  senderAgentName,
  senderAgentSessionId,
  responseRequirement,
  correlationId,
  onOpenAgentSettings,
}) {
  const displayContent = stripInterAgentHeader(content);
  const name = senderAgentName || "Unknown Agent";
  const sessionHref = senderAgentSessionId
    ? buildAgentChatUrl(senderAgentId, senderAgentSessionId)
    : `/agents/${senderAgentId}`;

  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const avatarClick = getAgentAvatarClickHandler(
    senderAgentId,
    onOpenAgentSettings
  );

  return (
    <Box sx={{ ...messageRowSx, pt: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <AgentAvatar agentId={senderAgentId} name={name} size={AVATAR_SIZE} onClick={avatarClick} />
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
            }}
          >
            <CallReceivedIcon sx={{ fontSize: 14, color: "primary.main" }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "text.secondary",
                flex: 1,
              }}
            >
              From:{" "}
              <Box
                component="span"
                onClick={(e) => setPopoverAnchor(e.currentTarget)}
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {name}
              </Box>
            </Typography>
            <AgentDetailPopover
              anchorEl={popoverAnchor}
              open={Boolean(popoverAnchor)}
              onClose={() => setPopoverAnchor(null)}
              agentId={senderAgentId}
            />
            {responseRequirement && (
              <ResponseRequirementBadge
                responseRequirement={responseRequirement}
              />
            )}
            {correlationId && (
              <CorrelationIdBadge correlationId={correlationId} />
            )}
            <Box
              component="a"
              href={sessionHref}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                backgroundColor: "background.paper",
                border: 1,
                borderColor: "divider",
                fontSize: "11px",
                fontWeight: 500,
                color: "primary.main",
                textDecoration: "none",
                flexShrink: 0,
                transition: "all 100ms ease",
                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: "divider",
                },
              }}
            >
              Open
              <OpenInNewIcon sx={{ fontSize: 11 }} />
            </Box>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            <MarkdownContent
              sx={{
                fontSize: "12px",
                color: "text.primary",
                lineHeight: 1.5,
                wordBreak: "break-word",
              }}
            >
              {displayContent}
            </MarkdownContent>
          </Box>
          {timestamp && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                {formatMessageTime(timestamp)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
